import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  AvatarGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import {
  OpenInFull as OpenInFullIcon,
  OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useFiles } from '../hooks/useFiles';
import { FileFullscreenViewer } from './FileFullscreenViewer';
import UploadVersionDialog from './UploadVersionDialog';
import { formatFileSize } from '../utils/formatFileSize';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const VersionRow = ({ version, fileId, isCurrent, onOpenFullscreen }) => { // onOpenFullscreen(versionNumber, filetype)
  const { t } = useTranslation(['files', 'common']);
  const { getVersionReaders } = useFiles();
  const [expanded, setExpanded] = useState(false);
  const [readers, setReaders] = useState([]);
  const [readersLoading, setReadersLoading] = useState(false);
  const [readersFetched, setReadersFetched] = useState(false);

  const handleExpand = async (_event, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && !readersFetched) {
      setReadersLoading(true);
      try {
        const res = await getVersionReaders(fileId, version.version_number, 50);
        const logs = res?.data?.data?.logs ?? [];
        const seen = new Set();
        setReaders(
          logs.filter(log => {
            const key = log.staff_member_id ?? log.staff_member_name;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
        );
      } catch {
        // non-critical
      } finally {
        setReadersLoading(false);
        setReadersFetched(true);
      }
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleExpand}
      elevation={0}
      disableGutters
      sx={{ '&:before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 2, minHeight: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          <Chip
            label={`v${version.version_number}`}
            size="small"
            color={isCurrent ? 'primary' : 'default'}
            variant={isCurrent ? 'filled' : 'outlined'}
            sx={{ flexShrink: 0 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {version.uploader_name ?? t('common:labels.unknown')}
              {' · '}
              {new Date(version.created_at).toLocaleDateString()}
            </Typography>
            {version.change_note && (
              <Typography variant="caption" color="text.secondary" noWrap display="block" fontStyle="italic">
                {version.change_note}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {formatFileSize(version.file_size)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
            <Tooltip title={t('files:actions.fullscreen')}>
              <IconButton size="small" onClick={() => onOpenFullscreen(version.version_number, version.filetype)}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('files:messages.open_new_tab')}>
              <IconButton
                size="small"
                onClick={() =>
                  window.open(`/files/view/${fileId}?version=${version.version_number}`, '_blank')
                }
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pb: 2 }}>
        {readersLoading ? (
          <CircularProgress size={16} />
        ) : readers.length === 0 ? (
          <Typography variant="caption" color="text.disabled">
            {t('files:messages.no_readers')}
          </Typography>
        ) : (
          <AvatarGroup
            max={8}
            sx={{ justifyContent: 'flex-start', '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.7rem' } }}
          >
            {readers.map(log => (
              <Tooltip
                key={log.staff_member_id ?? log.log_id}
                title={`${log.staff_member_name} · ${new Date(log.read_at).toLocaleString()}`}
              >
                <Avatar>{log.staff_member_name?.charAt(0)?.toUpperCase() ?? '?'}</Avatar>
              </Tooltip>
            ))}
          </AvatarGroup>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default function VersionHistoryDialog({ open, onClose, file, fileDetails, onVersionUploaded }) {
  const { t } = useTranslation(['files', 'common']);
  const { listFileVersions } = useFiles();
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [fullscreenVersionNumber, setFullscreenVersionNumber] = useState(null);
  const [fullscreenFiletype, setFullscreenFiletype] = useState(null);

  const fetchVersions = useCallback(async () => {
    if (!file?.file_id) return;
    setVersionsLoading(true);
    try {
      const res = await listFileVersions(file.file_id);
      setVersions(res?.data?.data?.versions ?? []);
    } catch {
      // non-critical
    } finally {
      setVersionsLoading(false);
    }
  }, [file?.file_id, listFileVersions]);

  useEffect(() => {
    if (open && file?.file_id) {
      fetchVersions();
    } else if (!open) {
      setVersions([]);
      setUploadOpen(false);
      setFullscreenVersionNumber(null);
      setFullscreenFiletype(null);
    }
  }, [open, file?.file_id, fetchVersions]);

  const handleVersionUploaded = () => {
    setUploadOpen(false);
    onVersionUploaded?.();
    fetchVersions();
  };

  const sortedVersions = [...versions].sort((a, b) => b.version_number - a.version_number);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle
          component="div"
          sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" noWrap>{file?.filename}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('files:labels.version_history')}
              {versions.length > 0 && ` (${versions.length})`}
            </Typography>
          </Box>
          {/* Without it, new versions can't be uploaded here (another department's shared file) */}
          {onVersionUploaded && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={() => setUploadOpen(true)}
              sx={{ flexShrink: 0, mt: 0.5 }}
            >
              {t('files:actions.upload_new_version')}
            </Button>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "320px", overflow: "auto" }} dividers>
          {versionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : sortedVersions.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ p: 3 }}>
              {t('files:messages.no_versions')}
            </Typography>
          ) : (
            sortedVersions.map(v => (
              <VersionRow
                key={v.version_id}
                version={v}
                fileId={file?.file_id}
                isCurrent={v.version_number === fileDetails?.current_version_number}
                onOpenFullscreen={(vn, ft) => { setFullscreenVersionNumber(vn); setFullscreenFiletype(ft); }}
              />
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common:buttons.close')}</Button>
        </DialogActions>
      </Dialog>

      {fullscreenVersionNumber !== null && (
        <FileFullscreenViewer
          fileId={file?.file_id}
          filename={`${file?.filename} (v${fullscreenVersionNumber})`}
          filetype={fullscreenFiletype}
          versionNumber={fullscreenVersionNumber}
          onClose={() => { setFullscreenVersionNumber(null); setFullscreenFiletype(null); }}
        />
      )}

      {uploadOpen && (
        <UploadVersionDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          file={fileDetails ? { ...file, current_version_number: fileDetails.current_version_number } : file}
          onSuccess={handleVersionUploaded}
        />
      )}
    </>
  );
}
